import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import fetchData from '@/utils/fetchData';
import useSmartlook from '@/hooks/useSmartlook';
import NoLatestLoanError from '@/entities/errors/NoLatestLoanError';

type LoanExtensionFee = {
  term: number;
  unit: string;
  fee: number;
  interest: number;
  feeWithoutInterest: number;
};

type Term = {
  value: number;
  termUnit: string;
};

export type Loan = {
  agreementLink: string;
  invoiceLink: string;
  paymentScheduleLink: string;
  loanExtensionFeesLink: string;
  debtPaymentScheduleLink: string;
  latestPaymentLink: string;
  paymentHolidayLink: string;
  fullRepaymentBarcodeLink: string;
  fullRepaymentAmountLink: string;
  extensionRepaymentLink: string;
  paymentsSummary: string;
  term: number;
  id: number;
  signDate: string;
  startDate: string;
  dueDate: string;
  closeDate: string;
  loanNumber: string;
  productCode: string;
  companyMainInfo: {
    name: string;
    registrationNr: string;
  };
  companyBank: {
    accountNumber: string;
    name: string;
    bic: string;
  };
  debtCollectLoanAgreement: boolean;
  renouncementLoanAgreement: boolean;
  overDueDays: number;
  openAmount: number;
  payNowAmount: number;
  payableAmount: number;
  delayedAmountDue: number;
  nonDelayedAmountDue: number;
  penaltyAmount: number;
  commissionAmount: number;
  initialCommissionAmount: number;
  debtPaymentScheduleFee: number;
  schedulePayment: number;
  nearestPaymentDate: string;
  loanExtensionFees: LoanExtensionFee[];
  autoRepay: boolean;
  amount: number;
  interestAmount: number;
  principalAmount: number;
  totalInvoicedAmount: number;
  totalOutstandingAmount: number;
  annualPercentageRate: number;
  interestRatePerYear: number;
  totalOutpaidAmount: number;
  agreementType: string;
  loanTerm: Term;
  totalTerm: Term;
  mainAgreementSignDate: string;
  gracePeriodInDays: number;
  discounts: {
    type: string;
    percent: number;
    amount: number;
  }[];
  extensionPossible: boolean;
  topUpAmount: number;
};

export function useLatestLoanQuery(enabled = true) {
  const { error } = useSmartlook();
  return useQuery<Loan>({
    queryKey: ['client', 'loans', 'latest'],
    queryFn: async () => {
      try {
        const response = await fetchData('/client/loans/latest');

        return response;
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 404) {
          throw new NoLatestLoanError();
        }

        error('[useLatestLoanQuery][GET /client/loans/latest]', e);

        throw new Error(`Something went wrong fetching latest loan, ${e}`);
      }
    },
    enabled,
  });
}
